/**
 * domains-core.js — pure, alias-free core for the customer `/my/domains`
 * page on the Domain object (Unified Certificate Service design v1.5.1 §15,
 * Phase 6.5). `services/domains-api.js`, the store and the views are thin over
 * this module so `node --test` exercises every load-bearing rule with a fake
 * `http`.
 *
 * Backend contract mirrored (dns-manager `feat/domains-object`, Phase 6.2):
 *   GET    /dns-manager/domains                       org-scoped Domain objects
 *   POST   /dns-manager/domains                       {domain, datacenter?, public_provider?, subdomain_policy?}
 *   GET    /dns-manager/domains/{d}
 *   PATCH  /dns-manager/domains/{d}                   datacenter | public_provider | provider_connection_id | subdomain_policy
 *   DELETE /dns-manager/domains/{d}
 *   POST   /dns-manager/domains/{d}/verify
 *   POST   /dns-manager/domains/{d}/delegation/check
 *   POST   /dns-manager/domains/{d}/private-lane/ensure
 *   GET    /dns-manager/domains/{d}/expected-records  ?hostnames=a,b&edge_ip=&observed=
 * `/dns-manager/customer/domains*` are the historic aliases: the client falls
 * back to them when the canonical route is missing (an older dns-manager).
 *
 * `edge_state` is operator-only and is never sent (A-25: cluster-api sets it).
 * `certificate_policy` (A-26) is operator-only too: read, never sent.
 *
 * cluster-api (A-26/A-27, design v1.6 §15.6):
 *   GET    /cluster/domains/{d}/delivery-target      {cluster, namespace, secret_name, reason?}
 *   422/409 certificate_policy_byo_only              {hostnames, domain, choices:["upload"]}
 *
 * No `@/` imports — keep this module pure.
 */
import { initialProgress, reduceProgress } from './certs-issue-core.js'
import { batchAllowed, isPermissionDenied } from './service-reach.js'
import {
  ACCESS_UNDECIDED_MESSAGE, NOT_VERIFIED_MESSAGE, PLATFORM_ZONE_MESSAGE, domainLimitError, fetchDomainAccess, readDomainsMeta
} from './personal-domain.js'

export const DOMAINS_BASE = '/dns-manager/domains'
export const LEGACY_BASE = '/dns-manager/customer/domains'

/** The four fields a customer may PATCH (Phase 6.2 `PATCHABLE_FIELDS`). */
export const PATCHABLE_FIELDS = ['datacenter', 'public_provider', 'provider_connection_id', 'subdomain_policy']

/** Public-lane choices the wizard offers (design §15.3). `own` is a form
 *  choice only: until P3b lands it is stored as `none` with a note. */
export const PROVIDER_CHOICES = [
  {
    key: 'constellix',
    label: 'Managed by the platform',
    hint: 'The platform writes your public records for you.'
  },
  {
    key: 'own',
    label: 'My own provider',
    hint: 'Route 53 or Cloudflare. Connecting a provider arrives with P3b — until then the platform treats the public side as yours to manage.'
  },
  {
    key: 'none',
    label: 'None — I manage DNS myself',
    hint: 'The platform never writes your public zone. It shows you the records to publish and checks them live.'
  }
]

export const DEFAULT_PROVIDER_CHOICE = 'none'

/** Records-tab public-lane modes. */
export const RECORDS_MODE = { MANAGED: 'managed', SELF: 'self', CONNECT: 'connect' }

export const A22_MESSAGE =
  'A platform zone cannot use provider "none" — tenant subdomains need a writable public provider.'

export const OWN_PROVIDER_NOTE =
  'Connecting your own provider arrives with P3b. For now this domain is stored as self-managed: the platform shows the records to publish and checks them live.'

export const TWO_LANE_NOTE =
  'Whatever manages your public DNS, the platform creates this zone on that datacenter\'s internal DNS so your apps can reach each other by name.'

function str(v) { return String(v === null || v === undefined ? '' : v).trim() }
function enc(v) { return encodeURIComponent(str(v)) }
export function normalizeDomain(v) { return str(v).toLowerCase().replace(/\.+$/, '') }

// ── typed errors ────────────────────────────────────────────────────────────

/** 403 from a record endpoint while the domain is still pending verification. */
export class DomainNotVerified extends Error {
  constructor(message, details = {}) {
    super(message || 'This domain is still pending verification — finish the TXT challenge to manage records.')
    this.name = 'DomainNotVerified'
    this.notVerified = true
    this.domain = details.domain || null
    this.detail = details
  }
}

/**
 * 403 from `GET /domains`: the permission plane refused `domain:viewer` for
 * this caller in this organisation (cs-packages `@require_permission` body
 * `{error: "forbidden", resource: "domain"}`). Org owners/admins hold
 * `domain.view` through the Admin role (auth-api A-31); a Member needs a
 * grant from them. Not a fault — the page says who can grant it.
 */
export class DomainAccessDenied extends Error {
  constructor(message) {
    super(message || DOMAIN_ACCESS_NOTE)
    this.name = 'DomainAccessDenied'
    this.accessDenied = true
  }
}

export const DOMAIN_ACCESS_NOTE =
  'Domains in this organisation are managed by its owner and admins. Ask one of them for domain access — they grant it from Members → Access.'

/** The exact check dns-manager's `GET /domains` makes (`@require_permission('domain', 'viewer')`). */
export const DOMAIN_VIEW_CHECK = Object.freeze({ resource: 'domain', action: 'viewer' })
export const PERMISSION_CHECK_PATH = '/auth/permissions/check-batch'

/**
 * Ask auth-api — the same resolver dns-manager asks — whether this caller may
 * list domains, BEFORE calling dns-manager, so a Member without the grant
 * never produces a 403 in the console. Never throws: true / false, or null
 * when the probe itself failed (the caller then lets the list call decide;
 * the server is the control either way).
 */
export async function probeDomainAccess(http) {
  try {
    const response = await http.post(PERMISSION_CHECK_PATH, { checks: [{ ...DOMAIN_VIEW_CHECK }] })
    const body = response && response.data
    const data = body && typeof body === 'object' && 'data' in body ? body.data : body
    return batchAllowed(data, DOMAIN_VIEW_CHECK.resource, DOMAIN_VIEW_CHECK.action)
  } catch (_) {
    return null
  }
}

/** 409 from the claim endpoint: another organisation owns this domain. */
export class DomainConflict extends Error {
  constructor(message, details = {}) {
    super(message || 'This domain is already claimed by another organization.')
    this.name = 'DomainConflict'
    this.conflict = true
    this.detail = details
  }
}

/**
 * 409 `public_lane_self_managed` (P-24): a public write on a domain whose
 * public provider is `none`. Carries the records the owner must publish.
 */
export class PublicLaneSelfManaged extends Error {
  constructor(message, details = {}) {
    super(message || 'This domain\'s public DNS is managed outside the platform — publish the expected records at your provider.')
    this.name = 'PublicLaneSelfManaged'
    this.selfManaged = true
    this.domain = details.domain || null
    this.expectedRecords = Array.isArray(details.expected_records) ? details.expected_records : []
    this.expectedRecordsUrl = details.expected_records_url || null
    this.instructions = details.instructions || null
    this.detail = details
  }
}

/** 422 policy refusals (A-22 `SUBDOMAIN_POLICY_REQUIRES_PUBLIC_PROVIDER` and kin). */
export class DomainPolicyError extends Error {
  constructor(message, code = null, details = {}) {
    super(message || A22_MESSAGE)
    this.name = 'DomainPolicyError'
    this.policy = true
    this.code = code
    this.detail = details
  }
}

const POLICY_CODES = [
  'SUBDOMAIN_POLICY_REQUIRES_PUBLIC_PROVIDER', 'SUBDOMAIN_POLICY_PLATFORM_ONLY',
  'INVALID_PUBLIC_PROVIDER', 'INVALID_SUBDOMAIN_POLICY', 'UNKNOWN_DOMAIN_FIELD'
]

/** `{ status, code, message, details }` from an axios-style error (CS envelope or bare). */
export function errorFacts(err) {
  const resp = err && err.response
  const status = resp ? resp.status : undefined
  const body = resp && resp.data && typeof resp.data === 'object' ? resp.data : {}
  const code = body.error_code || (typeof body.error === 'string' ? body.error : null) || body.code || null
  const message = (typeof body.message === 'string' && body.message) ||
    (typeof body.error === 'string' && body.error) || (err && err.message) || 'Request failed'
  const details = body.details || body.detail || {}
  return { status, code, message, details }
}

/**
 * Map an error to its typed twin, or return it unchanged. The mapping the
 * views rely on:
 *   409 public_lane_self_managed → PublicLaneSelfManaged
 *   409 (claim)                  → DomainConflict
 *   422 policy codes             → DomainPolicyError
 *   403 (records)                → DomainNotVerified
 */
export function typedDomainError(err, { domain = null, context = 'record' } = {}) {
  if (!err || !err.response) return err
  const { status, code, message, details } = errorFacts(err)
  if (status === 409 && code === 'public_lane_self_managed') {
    return new PublicLaneSelfManaged(message, { ...details, domain: details.domain || domain })
  }
  if ((status === 422 || status === 400) && POLICY_CODES.includes(code)) {
    return new DomainPolicyError(code === 'SUBDOMAIN_POLICY_REQUIRES_PUBLIC_PROVIDER' ? A22_MESSAGE : message, code, details)
  }
  // A platform zone (denvstack.com, caucasus.systems, …) can never be claimed as one's own.
  if (code === 'PLATFORM_ZONE_RESERVED' && (status === 403 || status === 422 || status === 409)) {
    return new DomainPolicyError(PLATFORM_ZONE_MESSAGE, code, details)
  }
  // dns-manager could not confirm the caller's plan: a retry line, never the 503 body.
  if (status === 503 && code === 'ACCESS_UNDECIDED') {
    const e = new Error(ACCESS_UNDECIDED_MESSAGE)
    e.code = code
    e.response = err.response
    return e
  }
  if (status === 409 && context === 'claim') {
    // A shared-tier caller's second domain: the plan sentence, never the 409 body.
    const limit = domainLimitError(err)
    if (limit) return limit
    return new DomainConflict(message, details)
  }
  if (status === 403 && context === 'record') {
    const lower = String(message).toLowerCase()
    if (code === 'resource_not_verified') {
      return new DomainNotVerified(NOT_VERIFIED_MESSAGE, { ...details, domain })
    }
    if (code === 'domain_not_verified' || code === 'verification_required' || code === 'DOMAIN_NOT_VERIFIED' || lower.includes('verif') || !code) {
      return new DomainNotVerified(
        message || `“${domain}” is still pending verification — finish the TXT challenge to manage records.`,
        { ...details, domain }
      )
    }
  }
  return err
}

/** Is this 404 "the route does not exist here" (older dns-manager) rather than "no such domain"? */
export function isRouteMissing(err) {
  if (!err || !err.response || err.response.status !== 404) return false
  const { code } = errorFacts(err)
  return !['CLAIM_NOT_FOUND', 'DOMAIN_NOT_FOUND', 'NOT_FOUND', 'not_found'].includes(String(code || ''))
}

/** Older dns-manager: `GET /domains` was the super-admin inventory (403 for customers). */
function isLegacyInventory(err) {
  if (!err || !err.response) return false
  if (err.response.status === 404) return isRouteMissing(err)
  return err.response.status === 403 && errorFacts(err).code === 'SUPER_ADMIN_REQUIRED'
}

// ── envelope ────────────────────────────────────────────────────────────────

export function unwrapEnvelope(response) {
  const body = response?.data
  if (body && typeof body === 'object' && 'success' in body) {
    if (body.success === false) {
      const err = new Error(body.message || body.error || 'Request failed')
      err.response = response
      throw err
    }
    return body.data
  }
  return body
}

function listFromData(data) {
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.domains)) return data.domains
  return []
}

/** Flatten a canonical claim answer (`{domain:{…}, verification_token, verification_record}`) into one row. */
export function flattenClaim(data) {
  if (!data || typeof data !== 'object') return data
  if (data.domain && typeof data.domain === 'object') {
    return {
      ...data.domain,
      verification_token: data.verification_token,
      verification_record: data.verification_record,
      idempotent: !!data.idempotent
    }
  }
  return data
}

/** Resolve a verify answer (200 verified / 202 pending / success:false pending) into a row. */
export function verifyResult(response) {
  const body = response?.data
  const httpStatus = response?.status ?? null
  if (body && typeof body === 'object' && body.success === false) {
    const code = body.error_code || body.code
    const status = body.details?.verification_status || body.verification_status
    const isPending = httpStatus === 202 || code === 'DOMAIN_NOT_VERIFIED' || status === 'pending'
    if (isPending) {
      return {
        ...(body.details || body.data || {}),
        verification_status: 'pending',
        message: body.error || body.message || 'TXT record not propagated yet — DNS may still be catching up.',
        _httpStatus: httpStatus
      }
    }
    const err = new Error(body.message || body.error || 'Verification failed')
    err.response = response
    throw err
  }
  const data = unwrapEnvelope(response)
  const flat = flattenClaim(data)
  return { ...(flat || {}), _httpStatus: httpStatus }
}

/** Strip anything that is not patchable (never `edge_state`, never `domain`). */
export function patchBody(fields = {}) {
  const out = {}
  PATCHABLE_FIELDS.forEach((k) => { if (k in (fields || {})) out[k] = fields[k] })
  return out
}

// ── the client ──────────────────────────────────────────────────────────────

/**
 * Build the Domain client over an axios-like `http` (`get/post/put/patch/
 * delete` returning `{ status, data }`, rejecting with `.response`).
 * Every canonical call falls back to the `/customer/domains` alias when the
 * canonical route is missing; `onFallback(name)` is told when that happens.
 */
export function createDomainsClient(http, { unwrap = unwrapEnvelope, onFallback = null } = {}) {
  if (!http) throw new Error('createDomainsClient: http client is required')
  const fell = (name) => { try { onFallback && onFallback(name) } catch (_) { /* ignore */ } }

  async function withFallback(name, primary, fallback, missing = isRouteMissing) {
    try {
      return await primary()
    } catch (err) {
      if (fallback && missing(err)) { fell(name); return fallback() }
      throw err
    }
  }

  return {
    /** GET /domains (falls back to GET /customer/domains) → Domain[] */
    async list() {
      try {
        return await withFallback('list',
          async() => listFromData(unwrap(await http.get(DOMAINS_BASE))),
          async() => listFromData(unwrap(await http.get(LEGACY_BASE))),
          isLegacyInventory)
      } catch (err) {
        if (isPermissionDenied(err, 'domain')) throw new DomainAccessDenied()
        throw err
      }
    },

    /**
     * GET /domains with the list's `meta` (`{scope, limit, used}` — null on an
     * older dns-manager or the alias). Same fallback and refusal as `list()`.
     */
    async listWithMeta() {
      try {
        return await withFallback('list',
          async() => {
            const response = await http.get(DOMAINS_BASE)
            const domains = listFromData(unwrap(response))
            return { domains, meta: readDomainsMeta(response && response.data) }
          },
          async() => ({ domains: listFromData(unwrap(await http.get(LEGACY_BASE))), meta: null }),
          isLegacyInventory)
      } catch (err) {
        if (isPermissionDenied(err, 'domain')) throw new DomainAccessDenied()
        throw err
      }
    },

    /** GET /domains/access → `{canManage, scope, limit, used, reason}` or null (never throws). */
    async access() {
      return fetchDomainAccess(http)
    },

    /**
     * POST /domains {domain, datacenter?, public_provider?, subdomain_policy?}
     * → flat row (+ verification_token, verification_record). The alias takes
     * only `{domain}`; placement is then applied with PATCH when possible.
     */
    async claim({ domain, datacenter = null, public_provider: publicProvider = null, subdomain_policy: subdomainPolicy = null } = {}) {
      const body = { domain: normalizeDomain(domain) }
      if (datacenter) body.datacenter = datacenter
      if (publicProvider) body.public_provider = publicProvider
      if (subdomainPolicy) body.subdomain_policy = subdomainPolicy
      try {
        return await withFallback('claim',
          async() => flattenClaim(unwrap(await http.post(DOMAINS_BASE, body))),
          async() => flattenClaim(unwrap(await http.post(LEGACY_BASE, { domain: body.domain }))),
          (err) => !!(err && err.response && err.response.status === 404))
      } catch (err) {
        throw typedDomainError(err, { domain: body.domain, context: 'claim' })
      }
    },

    /** GET /domains/{d} → Domain (alias has no single-read; falls back to the list). */
    async get(domain) {
      const d = normalizeDomain(domain)
      return withFallback('get',
        async() => { const data = unwrap(await http.get(`${DOMAINS_BASE}/${enc(d)}`)); return (data && data.domain) || data },
        async() => (listFromData(unwrap(await http.get(LEGACY_BASE))).find((r) => r.domain === d) || null))
    },

    /** PATCH /domains/{d} — the four placement fields only (canonical only). */
    async patch(domain, fields) {
      const body = patchBody(fields)
      try {
        const data = unwrap(await http.patch(`${DOMAINS_BASE}/${enc(normalizeDomain(domain))}`, body))
        return (data && data.domain) || data
      } catch (err) {
        throw typedDomainError(err, { domain, context: 'patch' })
      }
    },

    /** DELETE /domains/{d} (falls back to the alias). */
    async release(domain) {
      const d = normalizeDomain(domain)
      return withFallback('release',
        async() => unwrap(await http.delete(`${DOMAINS_BASE}/${enc(d)}`)),
        async() => unwrap(await http.delete(`${LEGACY_BASE}/${enc(d)}`)))
    },

    /** POST /domains/{d}/verify → row with `verification_status` ('pending' is soft). */
    async verify(domain) {
      const d = normalizeDomain(domain)
      return withFallback('verify',
        async() => verifyResult(await http.post(`${DOMAINS_BASE}/${enc(d)}/verify`)),
        async() => verifyResult(await http.post(`${LEGACY_BASE}/${enc(d)}/verify`)))
    },

    /** POST /domains/{d}/delegation/check → `{domain, zone, delegation}` */
    async checkDelegation(domain) {
      return unwrap(await http.post(`${DOMAINS_BASE}/${enc(normalizeDomain(domain))}/delegation/check`, {}))
    },

    /** POST /domains/{d}/private-lane/ensure → `{domain, zone, datacenter, private_lane}` */
    async ensurePrivateLane(domain) {
      return unwrap(await http.post(`${DOMAINS_BASE}/${enc(normalizeDomain(domain))}/private-lane/ensure`, {}))
    },

    /** GET /domains/{d}/expected-records?hostnames=&edge_ip=&observed= → `{records[], …}` */
    async expectedRecords(domain, { hostnames = [], edgeIp = null, observed = true } = {}) {
      const params = {}
      const names = (hostnames || []).map(normalizeDomain).filter(Boolean)
      if (names.length) params.hostnames = names.join(',')
      if (edgeIp) params.edge_ip = edgeIp
      if (observed === false) params.observed = 'false'
      return unwrap(await http.get(`${DOMAINS_BASE}/${enc(normalizeDomain(domain))}/expected-records`, { params }))
    }
  }
}

/** A small TTL cache around a fetcher (datacenters: 5 min). */
export function createTtlCache(fetcher, { ttlMs = 5 * 60 * 1000, now = () => Date.now() } = {}) {
  let value
  let at = -Infinity
  let inflight = null
  return {
    async get({ force = false } = {}) {
      if (!force && value !== undefined && now() - at < ttlMs) return value
      if (inflight) return inflight
      inflight = Promise.resolve().then(fetcher).then((v) => { value = v; at = now(); return v }).finally(() => { inflight = null })
      return inflight
    },
    clear() { value = undefined; at = -Infinity }
  }
}

// ── Domain presentation ─────────────────────────────────────────────────────

/** The public-lane label a customer reads (never a vendor name for the platform side). */
export function providerLabel(domain) {
  const p = domain && domain.public_provider
  if (p === 'none') return 'None — I manage DNS'
  if (p === 'route53') return 'My own provider (Route 53)'
  if (p === 'cloudflare') return 'My own provider (Cloudflare)'
  return 'Managed by the platform'
}

export function providerKind(domain) {
  const p = domain && domain.public_provider
  if (p === 'none') return 'none'
  if (p === 'route53' || p === 'cloudflare') return 'own'
  return 'managed'
}

/** Records-tab public-lane mode from the Domain's provider. */
export function recordsModeFor(domain) {
  const k = providerKind(domain)
  if (k === 'none') return RECORDS_MODE.SELF
  if (k === 'own') return RECORDS_MODE.CONNECT
  return RECORDS_MODE.MANAGED
}

/** `{ state, label, tone, hint }` for the private-lane dot. */
export function privateLaneState(domain) {
  const lane = (domain && domain.private_lane) || {}
  const dc = domain && domain.datacenter
  const zs = lane.zone_state || (dc ? 'missing' : 'not-placed')
  const legs = Array.isArray(lane.legs) ? lane.legs : []
  const failed = legs.filter((l) => l && l.error).map((l) => `${l.name || '?'}: ${l.error}`)
  if (zs === 'created') return { state: zs, label: 'created', tone: 'good', hint: `Internal zone created on ${dc || 'the datacenter'}’s DNS${legs.length ? ` (${legs.length} leg${legs.length === 1 ? '' : 's'})` : ''}.` }
  if (zs === 'coverage-gap') return { state: zs, label: 'coverage gap', tone: 'warn', hint: `No DNS leg in ${dc || 'this datacenter'} serves this zone yet — ask your operator.` }
  if (zs === 'not-placed') return { state: zs, label: 'not placed', tone: 'muted', hint: 'Pick a datacenter to create the internal zone.' }
  return { state: zs, label: 'missing', tone: 'bad', hint: failed.length ? failed.join('; ') : 'The internal zone could not be created — retry from the row.' }
}

/** `{ label, tone }` for A-25 edge state. */
export function edgeStateLabel(domain) {
  if (!domain || !domain.datacenter) return { label: 'no datacenter', tone: 'muted' }
  return domain.edge_state === 'ready' ? { label: 'ready', tone: 'good' } : { label: 'no edge yet', tone: 'warn' }
}

/** `{ label, tone, nameservers }` for the delegation state. */
export function delegationLabel(domain) {
  const d = (domain && domain.delegation) || {}
  const ns = Array.isArray(d.nameservers) ? d.nameservers : []
  if (d.state === 'delegated-to-platform') return { label: 'delegated to the platform', tone: 'good', nameservers: ns }
  if (d.state === 'external') return { label: 'external nameservers', tone: 'info', nameservers: ns }
  return { label: 'not checked', tone: 'muted', nameservers: ns }
}

// ── wizard ──────────────────────────────────────────────────────────────────

/** Exactly one environment datacenter → that; otherwise nothing is preselected. */
export function preselectDatacenter(envDatacenters = []) {
  const set = Array.from(new Set((envDatacenters || []).map(str).filter(Boolean)))
  return set.length === 1 ? set[0] : null
}

/**
 * The wizard's initial form. `datacenter` is preselected when the org's
 * environments live in exactly one datacenter; the choice stays editable
 * whenever more than one datacenter is available.
 */
export function wizardDefaults({ envDatacenters = [], datacenters = [] } = {}) {
  const pre = preselectDatacenter(envDatacenters)
  const available = (datacenters || []).map((d) => (typeof d === 'string' ? d : d && d.name)).filter(Boolean)
  return {
    domain: '',
    provider: DEFAULT_PROVIDER_CHOICE,
    datacenter: pre || (available.length === 1 ? available[0] : ''),
    datacenterPreselected: !!pre,
    datacenterEditable: !pre || available.length > 1,
    subdomainPolicy: 'owner-only'
  }
}

const FQDN_RE = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/

/** Errors map for step 1 (domain + provider). Empty = valid. */
export function validateWizardStep1(form = {}) {
  const errors = {}
  const d = normalizeDomain(form.domain)
  if (!d) errors.domain = 'Type the domain you own.'
  else if (d.startsWith('_')) errors.domain = 'No leading underscore — that namespace is reserved for verification records.'
  else if (d.startsWith('-') || d.includes('..')) errors.domain = 'Looks malformed — check for stray hyphens or double dots.'
  else if (!FQDN_RE.test(d)) errors.domain = 'That doesn’t look like a valid domain yet.'
  if (!['constellix', 'own', 'none'].includes(form.provider)) errors.provider = 'Choose who manages public DNS.'
  const a22 = a22Refusal(form)
  if (a22) errors.provider = a22
  return errors
}

/** A-22, client side: a platform-subdomain zone may not use provider none. */
export function a22Refusal(form = {}) {
  const stored = storedProvider(form.provider)
  return form.subdomainPolicy === 'platform' && stored === 'none' ? A22_MESSAGE : null
}

/** What the form choice becomes on the Domain (`own` → `none` until P3b). */
export function storedProvider(choice) {
  return choice === 'constellix' ? 'constellix' : 'none'
}

/** Errors map for step 2 (datacenter). A datacenter is optional (A-25 allows placing later). */
export function validateWizardStep2(form = {}, { datacenters = [] } = {}) {
  const errors = {}
  const names = (datacenters || []).map((d) => (typeof d === 'string' ? d : d && d.name)).filter(Boolean)
  if (form.datacenter && names.length && !names.includes(form.datacenter)) errors.datacenter = 'Pick a datacenter from the list.'
  return errors
}

/** The POST /domains body from the wizard form. */
export function claimBody(form = {}) {
  const body = { domain: normalizeDomain(form.domain), public_provider: storedProvider(form.provider) }
  if (str(form.datacenter)) body.datacenter = str(form.datacenter)
  if (form.subdomainPolicy && form.subdomainPolicy !== 'owner-only') body.subdomain_policy = form.subdomainPolicy
  return body
}

/** Datacenters seen through the org's environments: env.target_cluster_ref → options.clusters[].datacenter. */
export function environmentDatacenters(environments = [], clusters = []) {
  const byRef = {}
  ;(clusters || []).forEach((c) => { if (c && c.ref) byRef[String(c.ref)] = c.datacenter || null })
  const out = new Set()
  ;(environments || []).forEach((e) => {
    if (!e) return
    const direct = e.datacenter || (e.target && e.target.datacenter)
    const viaRef = e.target_cluster_ref !== undefined && e.target_cluster_ref !== null ? byRef[String(e.target_cluster_ref)] : null
    const dc = direct || viaRef
    if (dc) out.add(String(dc))
  })
  return Array.from(out)
}

/** Datacenter option rows from either `GET /datacenters` items or bare names. */
export function datacenterOptions(items = []) {
  const out = []
  const seen = new Set()
  ;(items || []).forEach((it) => {
    const name = typeof it === 'string' ? it : it && (it.name || it.id)
    if (!name || seen.has(name)) return
    seen.add(name)
    const label = typeof it === 'object' && it ? (it.display_name || it.label || name) : name
    const location = typeof it === 'object' && it ? (it.location || it.region || (it.geolocation && it.geolocation.city) || '') : ''
    out.push({ name, label, location })
  })
  return out
}

// ── expected records (provider none) ────────────────────────────────────────

/** Row status from one expected record's `observed`. */
export function expectedRowStatus(record) {
  if (!record) return 'unknown'
  if (record.expected === null || record.expected === undefined) return 'pending'
  const obs = record.observed
  if (!obs || typeof obs !== 'object') return 'unchecked'
  if (obs.error) return 'unknown'
  if (obs.match === true) return 'match'
  const values = Array.isArray(obs.values) ? obs.values : []
  return values.length ? 'mismatch' : 'missing'
}

/**
 * Reduce `GET /expected-records` into checklist rows + counts. Pure.
 *   rows[]: {type, name, expected, purpose, lane, note, observedValues, checkedAt, status}
 */
export function reduceExpectedRecords(records = []) {
  const rows = (records || []).map((r) => ({
    type: r.type || 'A',
    name: r.name || '',
    expected: r.expected === undefined ? null : r.expected,
    purpose: r.purpose || '',
    lane: r.lane || 'public',
    note: r.note || '',
    observedValues: r.observed && Array.isArray(r.observed.values) ? r.observed.values : [],
    checkedAt: (r.observed && r.observed.checked_at) || null,
    status: expectedRowStatus(r)
  }))
  const counts = { match: 0, mismatch: 0, missing: 0, pending: 0, unknown: 0, unchecked: 0 }
  rows.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1 })
  const actionable = rows.filter((r) => r.status !== 'pending')
  return {
    rows,
    counts,
    allGood: actionable.length > 0 && actionable.every((r) => r.status === 'match'),
    checkedAt: rows.map((r) => r.checkedAt).filter(Boolean).sort().pop() || null
  }
}

/** Zone-file-ish text for the copy-all button (one line per record). */
export function expectedRecordsText(rows = []) {
  return (rows || [])
    .filter((r) => r.expected !== null && r.expected !== undefined)
    .map((r) => `${r.name}\t${r.type}\t${r.expected}`)
    .join('\n')
}

/** The apex + www when nothing better is known. */
export function fallbackHostnames(domain) {
  const d = normalizeDomain(domain)
  return d ? [d, `www.${d}`] : []
}

/** Hostnames under `domain` from any candidate strings/objects; the apex + www otherwise. */
export function suggestHostnames(domain, candidates = []) {
  const d = normalizeDomain(domain)
  if (!d) return []
  const out = []
  const seen = new Set()
  ;(candidates || []).forEach((c) => {
    const raw = typeof c === 'string' ? c : c && (c.host || c.hostname || c.domain || c.name)
    const h = normalizeDomain(raw)
    if (!h || seen.has(h)) return
    if (h === d || h.endsWith(`.${d}`)) { seen.add(h); out.push(h) }
  })
  return out.length ? out : fallbackHostnames(d)
}

// ── SSL tab (the Issue view scoped to a domain) ─────────────────────────────

export function hostnameUnder(name, domain) {
  const h = normalizeDomain(name)
  const d = normalizeDomain(domain)
  return !!h && !!d && (h === d || h.endsWith(`.${d}`))
}

/** The org's registrations whose hostnames fall under `domain` (revoked excluded). */
export function registrationsUnderDomain(registrations = [], domain, { includeRevoked = false } = {}) {
  const d = normalizeDomain(domain)
  if (!d) return []
  return (registrations || []).filter((r) => {
    if (!r) return false
    if (!includeRevoked && r.state === 'revoked') return false
    if (normalizeDomain(r.domain_id) === d || normalizeDomain(r.domain_zone) === d) return true
    const names = Array.isArray(r.hostnames) ? r.hostnames : []
    return names.some((h) => hostnameUnder(typeof h === 'string' ? h : h && h.name, d))
  })
}

/** The eight-step ladder for a registration row, from its stored `progress`. */
export function ladderOf(registration) {
  const r = registration || {}
  return reduceProgress(initialProgress(r.id || null), {
    type: 'progress',
    data: {
      registration_id: r.id,
      state: r.state,
      steps: r.progress || r.steps || [],
      dns: r.dns || null,
      certificate: r.certificate || null,
      last_error: r.last_error || null
    }
  })
}

/** `{ label, tone }` for a registration state. */
export function registrationTone(state) {
  if (state === 'delivered') return { label: 'delivered', tone: 'good' }
  if (state === 'failed') return { label: 'failed', tone: 'bad' }
  if (state === 'revoked') return { label: 'revoked', tone: 'muted' }
  if (!state) return { label: 'registered', tone: 'muted' }
  return { label: String(state).replace(/_/g, ' '), tone: 'info' }
}

// ── certificate policy per Domain (A-26) ────────────────────────────────────

/**
 * `certificate_policy` on a Domain (design v1.6 §15.6, A-26). Operator-only:
 * set at seed for company-wildcard owners (`pensions.ge`, `msda.ge`) and never
 * sent from the customer page (`patchBody` does not carry it). Absent ⇒
 * `acme-allowed`.
 */
export const CERTIFICATE_POLICY = { ACME_ALLOWED: 'acme-allowed', BYO_ONLY: 'byo-only' }

export const UPLOAD_ONLY_LABEL = 'Upload only · owner-supplied certificate'

export const UPLOAD_ONLY_NOTE =
  'Your organisation supplies this certificate (e.g. a wildcard). Upload it here; the platform never issues or replaces it.'

export const NO_GRANT_NOTE = 'Ask your platform operator to enable certificate issuance for your organisation.'

/** The Domain's certificate policy; anything but `byo-only` reads as `acme-allowed`. */
export function certificatePolicyOf(domain) {
  const p = domain && typeof domain === 'object' ? str(domain.certificate_policy).toLowerCase() : ''
  return p === CERTIFICATE_POLICY.BYO_ONLY ? CERTIFICATE_POLICY.BYO_ONLY : CERTIFICATE_POLICY.ACME_ALLOWED
}

/** True when the platform may only accept an uploaded certificate for this Domain (A-26). */
export function isUploadOnly(domain) {
  return certificatePolicyOf(domain) === CERTIFICATE_POLICY.BYO_ONLY
}

/**
 * The SSL tab's gate: with `certificate.issue` the Generate / Upload actions
 * show; without it the list is read-only and the note tells the customer
 * whom to ask. For an upload-only Domain (A-26) Generate is never offered —
 * not even with the grant — while Upload stays grant-gated as before. Pure so
 * the matrix and the copy are tested.
 *
 *   → { canIssue, canGenerate, canUpload, uploadOnly, note, policyNote }
 */
export function sslGate(canIssue, domain = null) {
  const uploadOnly = isUploadOnly(domain)
  const granted = !!canIssue
  return {
    canIssue: granted,
    canGenerate: granted && !uploadOnly,
    canUpload: granted,
    uploadOnly,
    note: granted ? null : NO_GRANT_NOTE,
    policyNote: uploadOnly ? UPLOAD_ONLY_NOTE : null
  }
}

/** The source the Issue modal opens on: always `upload` for an upload-only Domain. */
export function initialIssueSource(requested = 'acme', { uploadOnly = false } = {}) {
  if (uploadOnly) return 'upload'
  return requested === 'upload' ? 'upload' : 'acme'
}

export const BYO_ONLY_CODE = 'certificate_policy_byo_only'

/**
 * 422 / 409 `certificate_policy_byo_only` from cluster-api (A-26): issuance,
 * renewal, rotation or reissue was refused for names under an upload-only
 * Domain. `choices` is what the customer may do instead (`["upload"]`).
 */
export class CertificatePolicyByoOnly extends Error {
  constructor(message, details = {}) {
    super(message || UPLOAD_ONLY_NOTE)
    this.name = 'CertificatePolicyByoOnly'
    this.code = BYO_ONLY_CODE
    this.uploadOnly = true
    this.status = details.status || null
    this.domain = details.domain || null
    this.hostnames = Array.isArray(details.hostnames) ? details.hostnames : []
    this.choices = Array.isArray(details.choices) && details.choices.length ? details.choices : ['upload']
    this.detail = details
  }
}

function policyFacts(body) {
  if (!body || typeof body !== 'object') return null
  const data = body.data && typeof body.data === 'object' ? body.data : {}
  const det = (body.details && typeof body.details === 'object' && body.details) ||
    (body.detail && typeof body.detail === 'object' && body.detail) || {}
  const codes = [body.error_code, body.error, body.code, data.error_code, data.error, data.code, det.error_code, det.code]
  if (!codes.some((c) => c === BYO_ONLY_CODE)) return null
  const pick = (k) => (det[k] !== undefined ? det[k] : data[k] !== undefined ? data[k] : body[k])
  return {
    message: (typeof body.message === 'string' && body.message) || null,
    domain: pick('domain') || null,
    hostnames: pick('hostnames'),
    choices: pick('choices')
  }
}

/**
 * The typed twin of a `certificate_policy_byo_only` refusal, or null.
 * Accepts an axios-style error (`err.response.{status,data}`), a bare
 * response, or a `certs-issue-core` ensure result (`{ ok, status, code,
 * message, data }`). Only 422 / 409 qualify.
 */
export function certificatePolicyError(errOrResult) {
  if (!errOrResult) return null
  if (errOrResult instanceof CertificatePolicyByoOnly) return errOrResult
  const resp = errOrResult.response || (errOrResult.data !== undefined && errOrResult.status !== undefined && errOrResult.ok === undefined ? errOrResult : null)
  let status
  let facts
  if (resp) {
    status = resp.status
    facts = policyFacts(resp.data)
  } else {
    status = errOrResult.status
    facts = policyFacts({
      code: errOrResult.code,
      message: errOrResult.message,
      data: errOrResult.data,
      details: errOrResult.details
    })
  }
  if (!facts || (status !== 422 && status !== 409)) return null
  return new CertificatePolicyByoOnly(facts.message, { ...facts, status })
}

// ── delivery target (A-27) ──────────────────────────────────────────────────

export const DELIVERY_SOURCE = { DOMAIN: 'domain', APPS: 'apps' }

/** `GET /cluster/domains/{d}/delivery-target` relative to the cluster axios base (`/api`). */
export function deliveryTargetUrl(domain) {
  return `/cluster/domains/${enc(normalizeDomain(domain))}/delivery-target`
}

function noTargetReason(domain) {
  const dc = domain && typeof domain === 'object' ? domain.datacenter : null
  return dc ? `Create an environment on ${dc} first.` : 'Create an environment first.'
}

/**
 * Resolve where a Generate / Upload for this Domain lands (A-27):
 *   - the Domain route answers with a target  → `{ source: 'domain', target, reason: null }`
 *   - the Domain route answers without one    → `{ source: 'domain', target: null, reason }`
 *   - the route does not exist here (404)     → `{ source: 'apps' }` (6.5 behaviour: the org's deployed apps)
 * Any other failure is thrown so the panel can show it and keep the action disabled.
 */
export async function loadDeliveryTarget(http, domain, { unwrap = unwrapEnvelope } = {}) {
  if (!http) throw new Error('loadDeliveryTarget: http client is required')
  const name = typeof domain === 'string' ? domain : domain && domain.domain
  let data
  try {
    data = unwrap(await http.get(deliveryTargetUrl(name)))
  } catch (err) {
    if (isRouteMissing(err)) return { source: DELIVERY_SOURCE.APPS, target: null, reason: null }
    throw err
  }
  const doc = data && typeof data === 'object' ? ((data.target && typeof data.target === 'object') ? { ...data.target, reason: data.reason } : data) : {}
  const cluster = str(doc.cluster)
  const namespace = str(doc.namespace)
  if (!cluster || !namespace) {
    return { source: DELIVERY_SOURCE.DOMAIN, target: null, reason: str(doc.reason || (data && data.reason)) || noTargetReason(domain) }
  }
  const secretName = str(doc.secret_name)
  return {
    source: DELIVERY_SOURCE.DOMAIN,
    target: { key: `${cluster}/${namespace}`, cluster, namespace, secretName, label: `${cluster} / ${namespace}` },
    reason: null
  }
}

/** One sentence for a Domain-resolved target: "Delivered into <namespace> as <secret_name>". */
export function deliveryLine(target) {
  if (!target || !target.namespace) return ''
  return target.secretName ? `Delivered into ${target.namespace} as ${target.secretName}` : `Delivered into ${target.namespace}`
}

/** Distinct (cluster, namespace) targets from the org's app rows. */
export function issueTargets(rows = []) {
  const out = []
  const seen = new Set()
  ;(rows || []).forEach((r) => {
    if (!r) return
    const cluster = str(r.cluster || (r.target && (r.target.cluster_name || r.target.cluster)))
    const namespace = str(r.namespace || (r.target && r.target.namespace))
    if (!cluster || !namespace || cluster === '—' || namespace === '—') return
    const key = `${cluster}/${namespace}`
    if (seen.has(key)) return
    seen.add(key)
    out.push({ key, cluster, namespace, label: `${cluster} / ${namespace}` })
  })
  return out
}

/** PATCH body flipping one hostname's Private toggle on a registration. */
export function privateToggleBody(registration, hostname, isPrivate) {
  const names = Array.isArray(registration && registration.hostnames) ? registration.hostnames : []
  return {
    hostnames: names.map((h) => {
      const name = typeof h === 'string' ? h : h && h.name
      const priv = typeof h === 'object' && h ? h.private !== false : true
      return { name, private: normalizeDomain(name) === normalizeDomain(hostname) ? !!isPrivate : priv }
    })
  }
}

// ── deep links ──────────────────────────────────────────────────────────────

export const TAB_KEYS = ['domains', 'records', 'ssl']

/** `{ tab, domain }` from a route query (`?tab=ssl&domain=example.com`). */
export function parseDeepLink(query = {}) {
  const tab = TAB_KEYS.includes(query && query.tab) ? query.tab : 'domains'
  const domain = normalizeDomain(query && query.domain) || null
  return { tab, domain }
}

/** The query to write back for the current state (omits defaults). */
export function deepLinkQuery({ tab = 'domains', domain = null } = {}) {
  const q = {}
  if (tab && tab !== 'domains') q.tab = tab
  if (domain) q.domain = domain
  return q
}
